import React, {useCallback, useState} from 'react'
import styled from '@emotion/styled'

import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {CurrencyType} from '@vio/core/src/localization/types/CurrencyType'

import {LanguageCurrencySelector} from '../../../../search/LanguageCurrencySelector'
import {StyledListItem} from '../commonStyles'

const WrapperPriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

interface Props {
  /** List of currencies to be displayed */
  currenciesList: CurrencyType[]
  /** The currently selected value */
  currentCurrency: string
  /** The callback for when a currency is selected. The new currency code will be passed. */
  onChangeCurrency: (code: string) => void
  /** List of currencies to be highlighted and shown first. */
  popularCurrenciesList: CurrencyType[]
  /** The callback when currency input is clicked */
  onClickCurrency?: () => void
  /** The callback when user changed the price display setting */
  onChangePriceDisplay?: (showTotal: boolean) => void
  /** The price display setting status */
  showTotalPrices?: boolean
  /** To show the price display setting */
  showPriceDisplaySetting?: boolean
  /** Should it render the include taxes */
  includeTaxes?: boolean
}

export const MobileCurrencySelector = ({
  currenciesList,
  currentCurrency,
  popularCurrenciesList,
  onChangeCurrency,
  onClickCurrency,
  onChangePriceDisplay,
  showTotalPrices,
  showPriceDisplaySetting,
  includeTaxes = false
}: Props) => {
  const [isCurrencySelectorOpen, setCurrencySelectorOpen] = useState(false)

  const handleCurrencyChange = (currency: string) => {
    onChangeCurrency(currency)
    setCurrencySelectorOpen(false)
  }

  const handleClick = () => {
    if (onClickCurrency) onClickCurrency()
    setCurrencySelectorOpen(true)
  }

  const handleChangePriceDisplay = useCallback(
    (showTotal: boolean) => {
      onChangePriceDisplay?.(showTotal)
      setCurrencySelectorOpen(false)
    },
    [onChangePriceDisplay]
  )

  return (
    <>
      <StyledListItem
        hasDivider
        as="button"
        onClick={handleClick}
        prefix={<Icon name="Money" />}
        title={
          <Text variant="labelM">
            <FormattedMessageWrapper
              id="currencyLabel"
              defaultMessage="Currency"
            />
          </Text>
        }
        suffix={
          showPriceDisplaySetting ? (
            <WrapperPriceDetails>
              <Text variant="bodyS">
                {showTotalPrices ? (
                  <FormattedMessageWrapper
                    id="totalPrice"
                    defaultMessage="Total price"
                  />
                ) : (
                  <FormattedMessageWrapper
                    id="nightlyPrice"
                    defaultMessage="Nightly price"
                  />
                )}
              </Text>

              <Text variant="labelM">{currentCurrency?.toUpperCase()}</Text>
            </WrapperPriceDetails>
          ) : (
            <Text variant="labelM">{currentCurrency?.toUpperCase()}</Text>
          )
        }
      />

      <LanguageCurrencySelector
        isMobile
        initialSelector="currency"
        isOpen={isCurrencySelectorOpen}
        onClose={() => setCurrencySelectorOpen(false)}
        availableLanguages={[]}
        selectedLanguage=""
        onChangeLanguage={() => ({})}
        hasCurrency={true}
        availableCurrencies={[...popularCurrenciesList, ...currenciesList]}
        selectedCurrency={currentCurrency}
        onChangeCurrency={handleCurrencyChange}
        onChangePriceDisplay={handleChangePriceDisplay}
        showTotalPrices={showTotalPrices}
        showPriceDisplaySetting={showPriceDisplaySetting}
        includeTaxes={includeTaxes}
      />
    </>
  )
}
